import { Link } from "react-router-dom";
import { Cross, Mail, MessageCircle } from "lucide-react";
import { BRAND, NAV_LINKS, DISCLAIMER } from "@/data/content";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Footer() {
  return (
    <footer className="border-t border-border bg-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5" data-testid="footer-logo">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand text-white">
                <Cross className="h-4 w-4" />
              </span>
              <span className="font-serif text-2xl tracking-tight text-ink">
                GetWell<span className="italic text-brand">Soon</span>
              </span>
            </Link>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted-foreground">
              End-to-end medical travel coordination for international patients
              and families seeking treatment in India.
            </p>
            <div className="mt-8 space-y-3">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email-link"
                className="flex items-center gap-3 text-sm font-semibold text-ink transition-colors hover:text-brand"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-light text-brand">
                  <Mail className="h-4 w-4" />
                </span>
                {BRAND.email}
              </a>
              <a
                href={BRAND.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp-link"
                className="flex items-center gap-3 text-sm font-semibold text-ink transition-colors hover:text-brand"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                  <MessageCircle className="h-4 w-4" />
                </span>
                WhatsApp: {BRAND.whatsappDisplay}
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              Explore
            </p>
            <ul className="mt-6 space-y-3">
              {[...NAV_LINKS, { to: "/contact", label: "Contact" }, { to: "/privacy-policy", label: "Privacy Policy" }].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${slug(l.label)}`}
                    className="text-sm text-ink/70 transition-colors hover:text-brand"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              Our commitment
            </p>
            <ul className="mt-6 space-y-3 text-sm text-ink/70">
              <li>Transparent, pressure-free coordination</li>
              <li>One dedicated coordinator per family</li>
              <li>Estimates based on your actual medical reports</li>
              <li>Support from first call to safe return</li>
            </ul>
          </div>
        </div>

        <div
          className="mt-14 rounded-2xl border border-border bg-pearl p-6 md:p-8"
          data-testid="footer-disclaimer"
        >
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.25em] text-brand">
            Important Disclaimer
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {DISCLAIMER}
          </p>
        </div>

        <div className="mt-10 flex flex-col justify-between gap-4 border-t border-border pt-8 md:flex-row md:items-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {BRAND.domain} — Medical travel
            coordination for international patients.
          </p>
          <p className="text-xs text-muted-foreground">
            Coordination services only. Not a hospital or medical provider.
          </p>
        </div>
      </div>
    </footer>
  );
}
